import { ref, computed } from "vue";
import { useStore } from "vuex";
import { options, columns } from "@/helpers/actionLogsTableHelper";

export default function useAppActionLogsTable() {
  const store = useStore();

  const loading = computed(() => store.getters["actionLogs/isLoading"]);
  const actionLogs = computed(() => store.getters["actionLogs/getActionLogs"]);
  const pagination = computed(() => store.getters["actionLogs/getPagination"]);
  const filters = computed(() => store.getters["actionLogs/getFilters"]);

  const selectedLogs = ref([]);

  const tableOptions = {
    ...options,
    pageLength: pagination.value.per_page,
    ajax: (data, callback) => {
      if (!loading.value) {
        callback({
          draw: data.draw,
          recordsTotal: pagination.value.total,
          recordsFiltered: pagination.value.total,
          data: actionLogs.value,
        });
      }
    },
    buttons: [
      {
        extend: "csvHtml5",
        text: "Export CSV",
        title: "App Action Logs",
        exportOptions: {
          columns: ":visible",
        },
        customizeData: (data) => {
          const dt = this;
          const selectedRows = dt.rows({ selected: true }).data().toArray();
          if (selectedRows.length > 0) {
            data.body = selectedRows.map((row) => Object.values(row));
          } else {
            const allRows = dt.rows().data().toArray();
            data.body = allRows.map((row) => Object.values(row));
          }
        },
      },
    ],
  };

  const fetchActionLogs = (additionalParams = {}) => {
    store.dispatch("actionLogs/fetchActionLogs", additionalParams);
  };

  const onPageChange = ({ event }) => {
    const info = event.dt.page.info();
    const newPage = info.page + 1;
    store.dispatch("actionLogs/setPagination", { current_page: newPage });
    fetchActionLogs();
  };

  const handleSearch = ({ dt }) => {
    const dataTable = dt.api ? dt.api : dt;
    if (dataTable && typeof dataTable.search === "function") {
      const searchValue = dataTable.search();
      if (filters.value.search !== searchValue) {
        store.dispatch("actionLogs/setFilters", {
          search: searchValue.trim() || "",
        });
        store.dispatch("actionLogs/setPagination", {
          current_page: 1,
        });
        fetchActionLogs();
      }
    }
  };

  const handleSortChange = ({ dt }) => {
    const dataTable = dt.api;
    if (dataTable && typeof dataTable.order === "function") {
      const order = dataTable.order();

      if (!order || order.length === 0) {
        if (
          filters.value.sort_by !== "time" ||
          filters.value.sort_order !== "desc"
        ) {
          store.dispatch("actionLogs/setFilters", {
            sort_by: "time",
            sort_order: "desc",
          });

          fetchActionLogs();
        }
        return;
      }

      const direction = order[0][1];
      const columnIndex = order[0][0];

      const sortField = direction === "" ? "time" : columns[columnIndex].data;
      const normalizedDirection = direction === "" ? "desc" : direction;

      if (
        filters.value.sort_by !== sortField ||
        filters.value.sort_order !== normalizedDirection
      ) {
        store.dispatch("actionLogs/setFilters", {
          sort_by: sortField,
          sort_order: normalizedDirection,
        });
        fetchActionLogs();
      }
    }
  };

  const handleLengthChange = ({ dt }) => {
    const newLength = dt._iDisplayLength;

    store.dispatch("actionLogs/setPagination", {
      per_page: newLength,
      current_page: 1,
    });

    fetchActionLogs();
    dt.api.page(pagination.value.current_page - 1).draw(false);
  };

  const onSelect = ({ dt }) => {
    const selectedRows = dt.rows({ selected: true }).data();
    selectedLogs.value = selectedRows.toArray();
  };

  const onDeselect = ({ dt }) => {
    const selectedRows = dt.rows({ selected: true }).data();
    selectedLogs.value = selectedRows.toArray();
  };

  const resetTable = () => {
    store.dispatch("actionLogs/resetActionLogs");
    selectedLogs.value = [];
  };

  return {
    actionLogs,
    tableOptions,
    columns,
    fetchActionLogs,
    resetTable,
    onPageChange,
    handleSearch,
    handleSortChange,
    handleLengthChange,
    onSelect,
    onDeselect,
  };
}
